import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from './auth.service'; // Adjust the path as needed
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

@Component({
  selector: 'ecom-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: false
})
export class ResetPasswordComponent implements OnInit {
  currentStep: 'email' | 'otp' | 'password' = 'email';
  email: string = '';
  errorMessage: string = '';
  successMessage: string = '';
  isLoading: boolean = false;
  isResendDisabled: boolean = false;
  resendCountdown: number = 0;
  showNewPassword : boolean = false;
  showConfirmPassword: boolean = false;

  emailForm: FormGroup;
  otpForm: FormGroup;
  resetForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    // Email step form
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    // OTP step form
    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9]{4,8}$/)]]
    });

    // Reset password step form
    this.resetForm = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', Validators.required]
      },
      { validators: this.passwordMatchValidator }
    );
  }

  ngOnInit(): void {}

  /** Custom validator to ensure passwords match */
  passwordMatchValidator(group: FormGroup): any {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { notSame: true };
  }

  /** Show success or error messages */
  private setMessage(message: string, isError: boolean = true, timeout: number = 5000): void {
    if (isError) {
      this.errorMessage = message;
      this.successMessage = '';
    } else {
      this.successMessage = message;
      this.errorMessage = '';
    }

    if (timeout > 0) {
      setTimeout(() => {
        if (this.errorMessage === message || this.successMessage === message) {
          this.errorMessage = '';
          this.successMessage = '';
          this.cdr.detectChanges();
        }
      }, timeout);
    }

    this.cdr.detectChanges();
  }

  /** Start resend OTP cooldown */
  private startResendCooldown(seconds: number = 30): void {
    this.isResendDisabled = true;
    this.resendCountdown = seconds;

    const interval = setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        this.isResendDisabled = false;
        clearInterval(interval);
      }
      this.cdr.detectChanges();
    }, 1000);
  }

  /** Step 1: Send OTP to email */
  onSubmitEmail(): void {
    if (this.emailForm.invalid || this.isLoading) {
      this.setMessage('Please enter a valid email.', true);
      return;
    }

    this.isLoading = true;
    this.email = this.emailForm.value.email.trim();

    this.authService.sendOtp(this.email).pipe(
      catchError(err => {
        this.handleError(err, 'Failed to send OTP');
        return throwError(() => err);
      })
    ).subscribe({
      next: () => {
        this.currentStep = 'otp';
        this.setMessage('OTP sent successfully.', false);
        this.startResendCooldown();
        this.emailForm.reset();
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /** Step 2: Verify OTP */
  onSubmitOtp(): void {
    if (this.otpForm.invalid || this.isLoading) {
      this.setMessage('Please enter a valid OTP.', true);
      return;
    }

    this.isLoading = true;

    this.authService.verifyOtp(this.email, this.otpForm.value.otp).pipe(
      catchError(err => {
        this.handleError(err, 'Invalid OTP');
        return throwError(() => err);
      })
    ).subscribe({
      next: () => {
        this.currentStep = 'password';
        this.setMessage('OTP verified successfully.', false);
        this.otpForm.reset();
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /** Step 3: Reset password */
  onSubmitReset(): void {
    if (this.resetForm.invalid || this.isLoading) {
      this.setMessage('Please enter valid passwords that match.', true);
      return;
    }

    this.isLoading = true;

    this.authService.resetPassword(this.email, this.resetForm.value.password).pipe(
      catchError(err => {
        this.handleError(err, 'Failed to reset password');
        return throwError(() => err);
      })
    ).subscribe({
      next: () => {
        this.setMessage('Password reset successfully. Redirecting to login...', false, 2000);
        this.resetForm.reset();
        this.isLoading = false;
        this.cdr.detectChanges();
        setTimeout(() => this.router.navigate(['/login']), 2000);
      }
    });
  }

  /** Resend OTP button */
  resendOtp(): void {
    if (this.isResendDisabled || this.isLoading) {
      return;
    }

    this.isLoading = true;

    this.authService.sendOtp(this.email).pipe(
      catchError(err => {
        this.handleError(err, 'Failed to resend OTP');
        return throwError(() => err);
      })
    ).subscribe({
      next: () => {
        this.setMessage('OTP resent successfully.', false);
        this.startResendCooldown();
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /** Centralized error handler */
  private handleError(err: any, defaultMessage: string): void {
    let message = defaultMessage;

    if (err?.status === 401) {
      message = 'Unauthorized: Please log in or contact support.';
    } else if (typeof err?.error === 'string' && err.error.trim()) {
      try {
        const parsed = JSON.parse(err.error);
        message = parsed.message || parsed.error || err.error;
      } catch (e) {
        message = err.error;
      }
    } else if (err?.error?.message) {
      message = err.error.message;
    } else if (err?.error?.error) {
      message = err.error.error;
    } else if (err?.message) {
      message = err.message;
    } else if (err?.statusText && err.statusText !== 'OK') {
      message = err.statusText;
    }

    this.setMessage(message, true);
    this.isLoading = false;
    this.cdr.detectChanges();
  }
  

toggleNewPassword() {
  this.showNewPassword = !this.showNewPassword;
}


toggleConfirmPassword() {
  this.showConfirmPassword = !this.showConfirmPassword;
}

}
